/*!
 * variant-submit-guard.js
 *
 * Fixes: "customer picks a size (e.g. 5ft) but the cart receives the default
 * (3ft) variant."
 *
 * Root cause: on products with more than 250 variants the theme uses
 * server-side option navigation (data-use-server-option-navigation="true").
 * Picking an option does NOT update input[name="id"] synchronously — it waits
 * for an async section re-render (~400ms) inside product-page.js (ie()), which
 * marks the section with `is-variant-loading` / aria-busy="true" during the
 * request. The Add-to-Cart button is not disabled in that window, so a customer
 * who selects a size and clicks Add-to-Cart within that ~400ms submits the
 * stale default variant (the first one = Single 3ft).
 *
 * Fix: intercept the product form's submit. If a variant re-render is in
 * flight, hold the submit, wait for the correct variant id to land, then submit
 * the live form. No-op when nothing is loading, so behaviour is unchanged for
 * products with <=250 variants and for submits made after the render settles.
 */
(function () {
  'use strict';
  if (window.__variantSubmitGuard) return;
  window.__variantSubmitGuard = true;

  function sectionIsLoading() {
    var section = document.querySelector('[data-product-section]');
    if (!section) return false;
    return (
      section.classList.contains('is-variant-loading') ||
      section.getAttribute('aria-busy') === 'true'
    );
  }

  function waitForRenderToSettle(timeoutMs) {
    return new Promise(function (resolve) {
      var startedAt = Date.now();
      (function tick() {
        if (!sectionIsLoading() || Date.now() - startedAt > timeoutMs) return resolve();
        setTimeout(tick, 30);
      })();
    });
  }

  // bypass = let the single follow-up submit through (avoids re-waiting / loops)
  var bypass = false;
  // waiting = a submit is already being held, so ignore re-entrant submits
  var waiting = false;

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-product-form')) return;

      // This is the follow-up submit we scheduled after the render settled.
      if (bypass) {
        bypass = false;
        return;
      }

      // No variant re-render in flight (or one is already being waited on):
      // let the browser submit normally.
      if (!sectionIsLoading() || waiting) return;

      // A variant selection is still rendering. Hold the submit until the
      // correct variant id has landed, then submit the live form.
      event.preventDefault();
      waiting = true;

      var atc = document.querySelector('[data-add-to-cart]');
      var sticky = document.querySelector('[data-sticky-atc-submit]');
      var label = atc ? atc.querySelector('[data-atc-main]') : null;
      var previousLabel = label ? label.textContent : '';
      var atcWasDisabled = atc ? atc.disabled : false;
      var stickyWasDisabled = sticky ? sticky.disabled : false;

      if (atc) {
        atc.setAttribute('aria-busy', 'true');
        atc.disabled = true;
      }
      if (sticky) sticky.disabled = true;
      if (label) label.textContent = 'Updating…';

      function finish() {
        waiting = false;
        var liveForm = document.querySelector('form[data-product-form]');
        if (!liveForm) {
          // Form vanished (section replaced mid-flight with no form) — restore UI.
          if (atc) { atc.disabled = atcWasDisabled; atc.removeAttribute('aria-busy'); }
          if (sticky) sticky.disabled = stickyWasDisabled;
          if (label) label.textContent = previousLabel;
          return;
        }
        bypass = true; // allow this single follow-up submit through the guard
        try {
          if (typeof liveForm.requestSubmit === 'function') {
            liveForm.requestSubmit();
          } else {
            bypass = false;
            liveForm.submit();
          }
        } catch (err) {
          bypass = false;
          liveForm.submit();
        }
      }

      // 3s ceiling: never block Add-to-Cart indefinitely, even if a render stalls.
      waitForRenderToSettle(3000).then(finish).catch(finish);
    },
    true // capture: run before other submit handlers
  );
})();
